import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { AiOutlineMail } from "react-icons/ai";
import { BiGroup, BiPencil } from "react-icons/bi";
import { IoApps } from "react-icons/io5";
import { Loading } from "../../../components";
import Avatar from "../../../components/avatar/Avatar";
import {
    useGetAccountQuery,
    AgentAssignmentPreference,
    useSaveUserSettingsMutation,
    GetAccountDocument,
    useSaveUserFirstLastNameMutation,
} from "../../../__generated__/api";
import Select from "../../../components/select/Select";

const ASSIGNMENT_PREFERENCES = [
    {
        label: "Automatically synchronize",
        value: AgentAssignmentPreference.AutoSynchronize,
    },
    {
        label: "Always available",
        value: AgentAssignmentPreference.Available,
    },
    {
        label: "Always unavailable",
        value: AgentAssignmentPreference.Unavailable,
    },

];

export default function AccountDetails(): ReactElement {
    const { data, loading: isLoading } = useGetAccountQuery();
    const [saveSettings] = useSaveUserSettingsMutation();
    const [saveUser] = useSaveUserFirstLastNameMutation();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");

    const user = data?.session?.user;

    const [assignmentPreference, setAssignmentPreference] = useState(data?.userSettings.assignmentPreference
            ?? AgentAssignmentPreference.AutoSynchronize);
    useEffect(() => {
        if (data?.userSettings?.assignmentPreference) {
            setAssignmentPreference(data.userSettings?.assignmentPreference);
        }
    }, [data]);

    useEffect(() => {
        if (!data || assignmentPreference === data?.userSettings.assignmentPreference) {
            return;
        }
        saveSettings({
            variables: {
                settings: {
                    assignmentPreference,
                },
            },
        });
    }, [assignmentPreference]);

    useEffect(() => {
        if (data?.session?.user) {
            setFirstName(data.session.user.firstName ?? "");
            setLastName(data.session.user.lastName ?? "");
        }
    }, [data?.session?.user.firstName, data?.session?.user.lastName]);

    if (isLoading) {
        return <Loading />;
    }
    if (!user) {
        return <h3 className="text-center text-light mt-3">Not Logged in</h3>;
    }

    const handleChangeUser = async (userId?: string): Promise<void> => {
        await saveUser({
            refetchQueries: [
                { query: GetAccountDocument },
            ],
            variables: {
                id: userId,
                user: {
                    firstName,
                    lastName,
                },
            },
        });
    };

    const applicationHandles = Object.entries(
        data?.session?.user?.handles?.filter(handle => handle.application)
            .groupBy(app => app.application?.applicationName ?? "")
        ?? {},
    );

    return (
        <div className="d-flex h-100vh">
            <div className="col-xl-3 col-lg-4 col-md-6 p-4">
                <div className="text-center">
                    <Avatar src={user?.avatarUrl as string} variant="lg" isCircle={false} />
                </div>
                <div className="mx-2">
                    <h3 className="mt-3 d-flex align-items-center">
                        {user.name}
                        {!isEditing && (
                            <button type="button" className="btn btn-none p-2" onClick={() => setIsEditing(true)}>
                                <BiPencil className="text-light" />
                            </button>
                        )}
                    </h3>
                    {isEditing && (
                        <div className="d-flex pt-2 pb-3">
                            <form
                                className="flex-grow-1"
                                onSubmit={event => {
                                    event.preventDefault();
                                    setIsEditing(false);
                                    handleChangeUser(user.id);
                                }}
                            >
                                <Form.Group className="mb-3">
                                    <Form.Label className="my-1 fw-bolder" htmlFor="addFirstName">First Name</Form.Label>
                                    <Form.Control
                                        id="addFirstName"
                                        className="form-control w-100 col"
                                        type="text"
                                        onChange={element => setFirstName(element.target.value)}
                                        value={firstName}
                                        required
                                        autoFocus
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder" htmlFor="addLastName">Last Name</Form.Label>
                                    <Form.Control
                                        id="addLastName"
                                        className="form-control w-100 col"
                                        type="text"
                                        onChange={element => setLastName(element.target.value)}
                                        value={lastName}
                                        required
                                    />
                                </Form.Group>
                                <p className="text-light">
                                    <small>
                                        Once you change your name, it may take 5-10 minutes to propagate to other Foqal systems.
                                        If you still see the old name after a few minutes, please contact support.
                                    </small>
                                </p>
                                <div className="d-flex justify-content-end mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-none text-danger me-3"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setFirstName(data?.session?.user?.firstName ?? "");
                                            setLastName(data?.session?.user?.lastName ?? "");
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-sm"
                                    >
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div>
                        {user.emailAddresses?.isNotEmpty && (
                            <div className="mt-3">
                                <div>
                                    <hr />
                                    <h6 className="pb-1">
                                        Email Addresses
                                    </h6>
                                </div>

                                {user.emailAddresses?.map(email => (
                                    <div key={email}>
                                        <AiOutlineMail className="text-light" />
                                        {" "}
                                        {email}
                                    </div>
                                ))}
                            </div>
                        )}
                        {user.userGroups.isNotEmpty && (
                            <div className="mt-3">
                                <div>
                                    <hr />
                                    <h6 className="pb-1">
                                        Groups
                                    </h6>
                                </div>

                                {user.userGroups?.map(group => (
                                    <div key={group.id}>
                                        <BiGroup className="text-light" />
                                        {" "}
                                        {group.name}
                                    </div>
                                ))}
                            </div>
                        )}
                        {applicationHandles.isNotEmpty && (
                            <div className="mt-3">
                                <div>
                                    <hr />
                                    <h6 className="pb-1">
                                        Application Connections
                                    </h6>
                                </div>

                                {applicationHandles.map(([appName, handles]) => (
                                    <div className="mb-3" key={appName}>
                                        {appName}
                                        {handles.map(handle => (

                                            <div
                                                className="
                                                    d-flex w-100 justify-content-between list-group-item align-items-center"
                                                key={handle.id}
                                            >
                                                <div className="d-flex flex-grow-1 align-items-center">
                                                    {handle.application && (
                                                        <div className="d-flex align-items-center">
                                                            <IoApps className="text-light me-2" />
                                                            {handle.application.displayText}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                    </div>
                                ))}

                            </div>
                        )}
                    </div>

                </div>
            </div>

            <div className="col shadow p-4 bg-white">
                <h5 className="">
                    Preferences
                </h5>

                <div className="row mt-3 ">
                    <Form.Group className="col-xl-6">

                        <Form.Label className="fw-bolder mt-0">Assignment Availability</Form.Label>

                        <Select
                            className="text-gray"
                            placeholder=""
                            onChange={value => setAssignmentPreference(value.value as AgentAssignmentPreference)}
                            options={ASSIGNMENT_PREFERENCES}
                            value={ASSIGNMENT_PREFERENCES.find(option => option.value === assignmentPreference)}
                        />
                        <Form.Text className="text-light">
                            This setting controlls if tickets should be assigned to you or not. By default, before
                            assigning a ticket to you, Foqal Agent will try to automatically determine if you are
                            available using your status in systems such as Slack.
                            However, you can override this behavior and set yourself as always
                            available or always unavailable.
                        </Form.Text>

                    </Form.Group>

                </div>

            </div>

        </div>
    );
}
